/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-crfs.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';

import DialogInformationBox from '@components/core/dialog-information-box/dialog-information-box';
import ImportErrors from '@components/core/import-errors';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from 'hooks';
import { StudyCrfFields } from 'queries/generated/fragments.generated';
import { GET_STUDY_CRF_ASSESSMENTS, GET_STUDY_CRFS, SAVE_CRFS } from 'queries/study-crfs';
import importCrfs from 'utils/importers/import-crfs';
import NotificationService from 'utils/notification';
import AddCrfAssessmentDialog from 'views/components/admin/add-crf-assessment';
import AddCrfDialog from 'views/components/admin/add-crf-dialog';
import { ImportCrfsInstructions, ImportCrfs } from 'views/components/admin/import-crfs';
import AdminStudyCRFsGrid from 'views/components/admin/study-crfs-grid/admin-study-crfs-grid';
import Button from 'views/components/core/buttons/button';
import ImportDialog from 'views/components/core/import-dialog';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import './admin-studies-main-styles.scss';
import Constants from '@utils/constants';
import { getStudy, getStudyVariables } from '@queries/generated/studies.generated';
import {
    studyCrfs,
    studyCrfsVariables,
    studyCrfAssessments,
    studyCrfAssessmentsVariables,
} from '@queries/generated/study-crfs.generated';
import { GET_STUDY } from '@queries/studies';

const AdminStudyCrfs = () => {
    const { state: localState } = useLocalState();
    const studyId = localState.selectedStudy?.id;
    const { t } = useTranslation();
    const showAddCrfDialog = useBoolean(false);
    const showAddCrfAssessmentDialog = useBoolean(false);
    const [currentCrf, setCurrentCrf] = useState(null);
    const [currentCrfAssessment, setCurrentCrfAssessment] = useState(null);
    const [studyCrfsData, setStudyCrfsData] = useState([]);
    const user = useAuthorization();
    const canEdit = user.canWriteSystemAdminLevel();
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    const [saveCrfs] = useMutation(SAVE_CRFS);
    // NOTE: I added the 'no-cache' option because for some reason it was returning wrong(cached) crfRules array.
    // It might be fixed by updating apollo/client(which last published 3 years ago) to @apollo/client
    const [loadData, { data, loading }] = useLazyQuery<studyCrfs, studyCrfsVariables>(GET_STUDY_CRFS, {
        fetchPolicy: 'no-cache',
    });
    const [loadStudy, { data: studyData, loading: studyLoading }] = useLazyQuery<getStudy, getStudyVariables>(
        GET_STUDY,
    );
    const { data: assessmentsData, loading: assessmentsLoading } = useQuery<
        studyCrfAssessments,
        studyCrfAssessmentsVariables
    >(GET_STUDY_CRF_ASSESSMENTS, { variables: { studyId: studyId ?? null } });
    const [importErrors, setImportErrors] = useState(null);
    const [importData, setImportData] = useState(null);
    const isImporting = useBoolean(false);
    const importCrfsDialogOpen = useBoolean(false);

    useEffect(() => {
        if (studyId) {
            loadData({ variables: { studyId } });
            loadStudy({ variables: { id: studyId } });
        }
    }, [studyId]);

    if (!studyId) return <h3>{t('common:noStudySelected')}</h3>;

    if (!user || !user.systemAdminLevel) return null;

    if (!studyId || assessmentsLoading || loading || studyLoading) return <Loading />;

    const doSaveCrfs = (items: StudyCrfFields[]) =>
        saveCrfs({
            variables: { studyId, crfs: items.map((crf: StudyCrfFields) => ({ ...crf })) },
            refetchQueries: [
                {
                    query: GET_STUDY_CRFS,
                    variables: {
                        studyId,
                    },
                },
            ],
        });

    return (
        <>
            {user && user.systemAdminLevel > 0 && (
                <ImportDialog
                    title={t('admin:importCRF')}
                    open={importCrfsDialogOpen.value}
                    disabled={isImporting.value}
                    button={
                        importData && importData.length ? (
                            <Button
                                id="import-risk-btn"
                                className="add-cv-btn add-risk-btn main-button"
                                disabled={isImporting.value}
                                onClick={() => {
                                    isImporting.setTrue();
                                    doSaveCrfs(importData).then(() => {
                                        isImporting.setFalse();
                                        setImportData(null);
                                        setImportErrors(null);
                                        importCrfsDialogOpen.setFalse();
                                    });
                                }}
                            >
                                {t('common:save')}
                            </Button>
                        ) : null
                    }
                    template={
                        isImporting.value ? (
                            <>
                                <div
                                    className="secondary"
                                    style={{ fontSize: 18, textAlign: 'center', margin: 'auto', marginBottom: 20 }}
                                >
                                    {t('ra:importing')}
                                </div>
                                <Loading float={false} />
                            </>
                        ) : importErrors && importErrors.length ? (
                            <ImportErrors errors={importErrors} />
                        ) : importData && importData.length ? (
                            <ImportCrfs data={importData} />
                        ) : (
                            <ImportCrfsInstructions />
                        )
                    }
                    onSelect={(files: any) => {
                        // Clear everything
                        setImportErrors(null);
                        setImportData(null);

                        if (files && files.length) {
                            isImporting.setTrue();
                            importCrfs(files[0])
                                .then(({ data: importedData, errors }: any) => {
                                    isImporting.setFalse();

                                    if (errors?.length) setImportErrors(errors);
                                    else if (importedData?.length) setImportData(importedData);
                                    else setImportErrors([{ line: null, message: 'No crfs found to import!' }]);
                                })
                                .catch((ex: any) => {
                                    console.log('Import exception: ', ex);
                                    isImporting.setFalse();
                                    setImportErrors([
                                        { line: null, message: ex.length > 0 ? ex[0].message : ex.message },
                                    ]);
                                });
                        }
                    }}
                    onClose={() => {
                        importCrfsDialogOpen.setFalse();
                        setImportErrors(null);
                        setImportData(null);
                    }}
                />
            )}
            {showAddCrfDialog.value && (
                <AddCrfDialog
                    isSysAdmin={isSysAdmin}
                    crfs={studyCrfsData}
                    currentCrf={currentCrf}
                    studyId={studyId}
                    close={(action: string) => {
                        if (action === 'error') {
                            NotificationService.showNotification(t('errorMessage'));
                        } else {
                            if (action === 'success' || action === 'edit') {
                                loadData({ variables: { studyId } });
                                NotificationService.showNotification(
                                    action === 'success'
                                        ? t('admin:successfulCreateCRF')
                                        : t('admin:successfulUpdateCRF', { id: currentCrf?.recordId }),
                                );
                            }
                            setCurrentCrf(null);
                            showAddCrfDialog.setFalse();
                        }
                    }}
                />
            )}
            <AddCrfAssessmentDialog
                studyId={studyId}
                currentCrf={currentCrf}
                currentCrfAssessment={currentCrfAssessment}
                assessments={assessmentsData?.crfAssessments}
                open={showAddCrfAssessmentDialog.value}
                close={(action: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(
                                t('admin:successfulCreateAssessment', { id: currentCrf.recordId }),
                            );
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:successfulUpdateAssessment', { id: currentCrfAssessment.recordId }),
                            );
                        setCurrentCrf(null);
                        setCurrentCrfAssessment(null);
                        showAddCrfAssessmentDialog.setFalse();
                    }
                }}
            />
            <PageTitle text={t('admin:manageCRFs')} />
            <div className="info-box-wrapper">
                <DialogInformationBox
                    topText={t(
                        studyData?.study?.assessmentAssociation
                            ? 'admin:manageAssessmentsAssociationTrue'
                            : 'admin:manageAssessmentsAssociationFalse',
                    )}
                />
            </div>
            <div className="admin-study-main-container main-crf-container">
                <AdminStudyCRFsGrid
                    canEdit={canEdit}
                    isSysAdmin={isSysAdmin}
                    loadedData={data?.crfs}
                    assessments={assessmentsData?.crfAssessments}
                    studyCrfs={studyCrfsData}
                    setStudyCrfs={setStudyCrfsData}
                    setCurrentCrf={setCurrentCrf}
                    showAddCrfDialog={showAddCrfDialog.setTrue}
                    assessmentAssociation={studyData?.study?.assessmentAssociation}
                />
            </div>
        </>
    );
};

export default AdminStudyCrfs;
